import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  Alert,
  Box,
} from "@mui/material";
import {
  getAgents,
  linkAgentStay,
  updateAgentStay,
  deleteAgentStay,
} from "../Agents/api/agentsApi";
import { agentOptionOf, filterAgents } from "../Agents/LinkAgentDialog";
import CommissionFields, { rateIsRequired, rateIsValid } from "../Agents/CommissionFields";
import AgentFormDialog from "../Agents/AgentFormDialog";
import { settleWithConcurrency } from "./utils/concurrency";
import { planAgentChanges } from "./utils/groupAgent";

// Put ONE agent on every member stay of a booking group, from the group side. The
// per-stay endpoints do the work (link / update snapshot / delete-then-link for a
// move) — same as the Agents module's "Add by group", just pointed the other way:
// the group is known, the agent is picked.
//
// rows: [{ studentId, name, stay }] — stay carries agentId/agentName/commission/
// commissionRate (V152). Rows without a resolved stay are skipped upstream.

// "Linked 3, moved 2 and updated commission on 10 stays for Alex Brown." — the
// result line says what the save actually did rather than calling it all a link.
function describeApply(plan, agentLabel) {
  const parts = [];
  if (plan.link.length) parts.push(`linked ${plan.link.length}`);
  if (plan.move.length) parts.push(`moved ${plan.move.length}`);
  if (plan.update.length) parts.push(`updated commission on ${plan.update.length}`);
  const total = plan.link.length + plan.move.length + plan.update.length;
  const list =
    parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : parts[0];
  return `${list.charAt(0).toUpperCase()}${list.slice(1)} stay${total === 1 ? "" : "s"} for ${agentLabel}.`;
}

export default function GroupAgentDialog({ open, onClose, rows, summary, groupName, onSaved }) {
  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState(null);
  const [commission, setCommission] = useState("GROSS");
  const [commissionRate, setCommissionRate] = useState("20");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [newAgentOpen, setNewAgentOpen] = useState(false);

  // The incumbent, when the group already has exactly one agent — the dialog opens
  // on them with the snapshot most of the stays carry.
  const incumbent = summary?.agents?.length === 1 ? summary.agents[0] : null;

  const loadAgents = useCallback(
    () =>
      getAgents()
        .then((data) => {
          const list = Array.isArray(data) ? data : [];
          setAgents(list);
          return list;
        })
        .catch(() => {
          setAgents([]);
          return [];
        }),
    []
  );

  // Reset on OPEN only. rows/incumbent are refreshed by onSaved after a save, and
  // that must not wipe the result banner or the picked agent.
  const latest = useRef({ rows, incumbent });
  latest.current = { rows, incumbent };
  useEffect(() => {
    if (!open) return;
    const { rows: rs, incumbent: inc } = latest.current;
    setError(null);
    setResult(null);
    setSaving(false);
    setConfirmUnlink(false);
    setAgent(null);
    const linked = inc
      ? rs.find((r) => r.stay?.agentId != null && String(r.stay.agentId) === String(inc.agentId))
      : null;
    setCommission(linked?.stay?.commission || "GROSS");
    setCommissionRate(
      linked?.stay?.commissionRate != null ? String(linked.stay.commissionRate) : "20"
    );
    loadAgents().then((list) => {
      if (!inc) return;
      const found = list.find((a) => String(a.id) === String(inc.agentId));
      setAgent(
        found
          ? agentOptionOf(found)
          : { id: inc.agentId, agent: null, label: inc.agentName, search: "" }
      );
    });
  }, [open, loadAgents]);

  const handleAgentChange = (picked) => {
    setAgent(picked);
    setError(null);
    if (!picked?.agent) return;
    // A different agent defaults the snapshot from their record; the incumbent
    // keeps whatever the stays already carry.
    if (incumbent && String(picked.id) === String(incumbent.agentId)) return;
    setCommission(String(picked.agent.commission || "GROSS").toUpperCase());
    setCommissionRate(picked.agent.commissionRate != null ? String(picked.agent.commissionRate) : "20");
  };

  const handleAgentCreated = async (saved) => {
    const list = await loadAgents();
    const found = list.find((a) => String(a.id) === String(saved?.id));
    if (found) handleAgentChange(agentOptionOf(found));
    else if (saved?.id) handleAgentChange(agentOptionOf(saved));
  };

  const options = useMemo(
    () => agents.map(agentOptionOf).sort((a, b) => a.label.localeCompare(b.label)),
    [agents]
  );

  const target = useMemo(
    () =>
      agent
        ? {
            agentId: agent.id,
            commission,
            commissionRate: rateIsRequired(commission) ? Number(commissionRate) : 0,
          }
        : null,
    [agent, commission, commissionRate]
  );
  const plan = useMemo(() => (target ? planAgentChanges(rows, target) : null), [rows, target]);
  const changes = plan ? plan.link.length + plan.move.length + plan.update.length : 0;
  const canSave = !!target && rateIsValid(commission, commissionRate) && changes > 0 && !saving;

  const snapshotBody = () => ({
    commission: target.commission,
    commissionRate: target.commissionRate,
  });

  const runAll = async (items, fn) => {
    // Two at a time — the agent endpoints are cheap, and it keeps the audit log readable.
    const settled = await settleWithConcurrency(items, fn, 2);
    return {
      ok: settled.filter((r) => r.status === "fulfilled").length,
      failed: settled
        .filter((r) => r.status === "rejected")
        .map((r) => ({ name: r.item.name, message: String(r.reason?.message || r.reason) })),
    };
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const outcome = await runAll(
        [...plan.link, ...plan.move, ...plan.update],
        async (r) => {
          const s = r.stay;
          if (s.agentId == null) {
            await linkAgentStay(target.agentId, s.stayId, snapshotBody());
          } else if (String(s.agentId) === String(target.agentId)) {
            await updateAgentStay(target.agentId, s.stayId, snapshotBody());
          } else {
            // No single-call move: drop the incumbent, then link. If the second
            // half fails the stay is left unlinked — the report says which.
            await deleteAgentStay(s.agentId, s.stayId);
            try {
              await linkAgentStay(target.agentId, s.stayId, snapshotBody());
            } catch (e) {
              throw new Error(`unlinked from ${s.agentName || "the previous agent"} but linking failed (${e.message}) — now has no agent`);
            }
          }
        }
      );
      setResult({ text: describeApply(plan, agent.label), ...outcome });
      await onSaved?.();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlinkAll = async () => {
    setSaving(true);
    setError(null);
    try {
      const linked = rows.filter((r) => r.stay?.agentId != null);
      const outcome = await runAll(linked, (r) => deleteAgentStay(r.stay.agentId, r.stay.stayId));
      setResult({ text: `Unlinked ${outcome.ok} stay${outcome.ok === 1 ? "" : "s"}.`, ...outcome });
      setConfirmUnlink(false);
      await onSaved?.();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const anyLinked = rows.some((r) => r.stay?.agentId != null);
  // Fifteen placeholder names in one line is noise — show a few, count the rest.
  const nameList = (list, max = 6) => {
    const names = list.map((r) => r.name);
    return names.length <= max
      ? names.join(", ")
      : `${names.slice(0, max).join(", ")} and ${names.length - max} more`;
  };

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>Agent for {groupName || "this group"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {error ? <Alert severity="error">{String(error.message || error)}</Alert> : null}

            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Applies to every student&apos;s group stay ({rows.length} stay{rows.length === 1 ? "" : "s"}).
              Stays already on the chosen agent keep their own commission unless it differs
              from the terms below.
            </Typography>

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Autocomplete
                sx={{ flex: 1 }}
                options={options}
                value={agent}
                onChange={(_, v) => handleAgentChange(v)}
                getOptionLabel={(o) => o.label || ""}
                filterOptions={filterAgents}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} label="Agent" placeholder="Name, email or phone" size="small" />
                )}
              />
              <Button size="small" sx={{ mt: 0.5 }} onClick={() => setNewAgentOpen(true)}>
                ＋ New agent
              </Button>
            </Stack>

            <CommissionFields
              idPrefix="group-agent"
              commission={commission}
              onCommissionChange={setCommission}
              rate={commissionRate}
              onRateChange={setCommissionRate}
            />

            {/* Reserve the plan's height so the footer doesn't jump under the cursor
                as the plan appears/changes (a Cancel click became an Apply). */}
            {plan ? (
              <Box sx={{ minHeight: 96 }}>
                <Typography variant="subtitle2">What will change</Typography>
                {plan.link.length > 0 && (
                  <Typography variant="body2" color="success.main">
                    + Link {plan.link.length}: {nameList(plan.link)}
                  </Typography>
                )}
                {plan.move.length > 0 && (
                  <Typography variant="body2" color="warning.main">
                    ↷ Move {plan.move.length} from another agent: {nameList(plan.move)}
                  </Typography>
                )}
                {plan.update.length > 0 && (
                  <Typography variant="body2" color="warning.main">
                    ~ Change commission on {plan.update.length}: {nameList(plan.update)}
                  </Typography>
                )}
                {plan.keep.length > 0 && (
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    = {plan.keep.length} already as chosen
                  </Typography>
                )}
                {changes === 0 && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Every stay is already on {agent.label} at these terms.
                  </Alert>
                )}
              </Box>
            ) : null}

            {result ? (
              <Alert severity={result.failed.length ? "warning" : "success"}>
                {result.text}
                {result.failed.length
                  ? ` ${result.failed.length} failed: ${result.failed
                      .map((f) => `${f.name} (${f.message})`)
                      .join("; ")}`
                  : ""}
              </Alert>
            ) : null}

            {confirmUnlink ? (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={handleUnlinkAll} disabled={saving}>
                    Yes, unlink all
                  </Button>
                }
              >
                Remove the agent from every linked stay in this group? Their commission on
                those stays goes with it.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          {anyLinked ? (
            <Button color="error" onClick={() => setConfirmUnlink(true)} disabled={saving || confirmUnlink}>
              Unlink all
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} disabled={saving}>
            {result ? "Close" : "Cancel"}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave} sx={{ minWidth: 180 }}>
            {saving ? "Saving…" : changes > 0 ? `Apply to ${changes} stay${changes === 1 ? "" : "s"}` : "Apply"}
          </Button>
        </DialogActions>
      </Dialog>

      <AgentFormDialog
        open={newAgentOpen}
        mode="create"
        onClose={() => setNewAgentOpen(false)}
        onSaved={handleAgentCreated}
      />
    </>
  );
}
